import { useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import {
  Calendar,
  Edit,
  ExternalLink,
  Mail,
  MapPin,
  Package,
  Phone,
  User,
} from "lucide-react";
import { PrintShipmentLabelButton } from "./components/print-shipment-label-button";
import { Link } from "react-router";
import {
  shipmentStatusColors,
  getOrderStatus,
  isOrderAwaitingPayment,
  getRecipientFromOrder,
} from "@/api/use-customer-orders-v2";
import { ShipmentStatusBadge } from "@/components/shipment-status-badge";
import { type Order, ShipmentStatus } from "@/api.types.v2";
import { useRequestCancellation } from "@/api/use-request-cancellation";
import { toast } from "@/hooks/use-toast";
import { PayOrderDialog } from "./pay-order-dialog";
import { CancellationPendingNotice } from "./cancellation-pending-notice";
import { ShipmentChangeRequestDialog } from "./shipment-change-request-dialog";
import { useTranslation } from "react-i18next";
import {
  canRequestCustomerShipmentCancellation,
  isPreCollectionShipment,
} from "@/api/shipment-action-eligibility";

import { hasOrderRefund, OrderPaymentSummary } from "./order-payment-summary";

function formatCurrency(cents?: number): string {
  if (cents === undefined || cents === null) return "-";
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(cents / 100);
}

function ProofPhotoPreview({
  photo,
  alt,
}: {
  photo: { url: string; thumbnailUrl?: string | null };
  alt: string;
}) {
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  return (
    <img
      src={
        !thumbnailFailed && photo.thumbnailUrl ? photo.thumbnailUrl : photo.url
      }
      alt={alt}
      onError={() => setThumbnailFailed(true)}
      className="w-20 h-20 object-cover rounded-md border border-default-200"
    />
  );
}

function getSenderFromOrder(order: Order): {
  name: string;
  fullAddress: string;
  phone?: string;
  email?: string;
} | null {
  if (!order.shipments?.length) return null;
  const shipment = order.shipments[0];

  const fromAddress = shipment.addresses?.find((a) => a.type === "FROM");
  if (fromAddress) {
    const addr = fromAddress.address;
    const parts = [
      fromAddress.addressLine1 ?? addr?.line1,
      fromAddress.addressLine2 ?? addr?.line2,
      fromAddress.addressCity ?? addr?.city,
      fromAddress.addressProvince ?? addr?.province,
      fromAddress.addressPostalCode ?? addr?.postalCode,
    ].filter(Boolean);
    if (parts.length > 0 || fromAddress.contactName) {
      return {
        name: fromAddress.contactName ?? fromAddress.name ?? "",
        fullAddress: parts.join(", "),
        phone: fromAddress.contactPhone ?? fromAddress.phone,
        email: fromAddress.contactEmail ?? fromAddress.email,
      };
    }
  }

  const pickupTask = shipment.tasks?.find((t) => t.type === "CUSTOMER_PICKUP");
  if (pickupTask?.addressLine1 || pickupTask?.contactName) {
    const parts = [
      pickupTask.addressLine1,
      pickupTask.addressLine2,
      pickupTask.addressCity,
      pickupTask.addressProvince,
      pickupTask.addressPostalCode,
    ].filter(Boolean);
    return {
      name: pickupTask.contactName ?? "",
      fullAddress: parts.join(", "),
      phone: pickupTask.contactPhone,
    };
  }

  // Pattern B: source of truth is the role-tagged addresses[] above.
  // shipment.shipFrom fallback removed (task #258) — if the addresses[]
  // lookup returned nothing, the sender genuinely isn't on the response.
  return null;
}

const CHANGEABLE_STATUSES: ShipmentStatus[] = [
  ShipmentStatus.LABEL_READY,
  ShipmentStatus.PICKED_UP,
  ShipmentStatus.IN_TRANSIT,
  ShipmentStatus.OUT_FOR_DELIVERY,
];

function canRequestChanges(status: ShipmentStatus | null): boolean {
  if (!status) return false;
  return CHANGEABLE_STATUSES.includes(status);
}

interface OrderCardDetailedProps {
  order: Order;
}

export function OrderCardDetailed({ order }: OrderCardDetailedProps) {
  const { t, i18n } = useTranslation("app");
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [changeRequestDialogOpen, setChangeRequestDialogOpen] = useState(false);
  const status = getOrderStatus(order);
  const sender = getSenderFromOrder(order);
  const recipient = getRecipientFromOrder(order);
  const shipment = order.shipments?.[0];
  const packages = shipment?.packages ?? [];
  const labelUrl = shipment?.packages?.find((p) => p.labelUrl)?.labelUrl;
  const pickupInstructions =
    shipment?.addresses?.find((a) => a.type === "FROM")?.instructions?.trim() ||
    null;
  const deliveryInstructions = shipment?.deliveryNotes?.trim() || null;
  const requestCancellation = useRequestCancellation({
    onSuccess: (data) => {
      toast({
        title: t("orders.toastCancellationTitle"),
        description: data.message,
      });
      setCancelDialogOpen(false);
    },
    onError: () =>
      toast({
        variant: "destructive",
        title: t("orders.toastCancellationError"),
      }),
  });

  const hasPendingCancellation = order.shipments?.some(
    (s) => s.cancellationRequestedAt
  );

  const showPay = isOrderAwaitingPayment(order);
  const showPrint =
    status === ShipmentStatus.LABEL_READY && !hasPendingCancellation;
  const showCancel =
    canRequestCustomerShipmentCancellation(status) && !hasPendingCancellation;
  const showChangeRequest =
    canRequestChanges(status) && !hasPendingCancellation;

  const handleCancelConfirm = () => {
    requestCancellation.mutate({ orderId: order.id });
  };

  return (
    <div className="space-y-6">
      {hasPendingCancellation && <CancellationPendingNotice />}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <Link
            to={`/orders/${order.id}`}
            className="text-xl font-bold font-mono text-default-900 hover:text-primary"
          >
            SHP-{shipment?.shipmentNumber ?? "—"}
          </Link>
          <div className="flex items-center gap-1.5 mt-1 text-sm text-default-500">
            <Calendar className="h-3.5 w-3.5" />
            {new Date(order.createdAt).toLocaleDateString(
              i18n.language === "zh" ? "zh-CN" : "en-CA",
              {
                month: "short",
                day: "numeric",
                year: "numeric",
              }
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {status && (
            <ShipmentStatusBadge
              status={status}
              paymentStatus={order.paymentStatus}
              className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold text-white ${shipmentStatusColors[status] || "bg-default-500"}`}
            />
          )}
        </div>
      </div>

      <div className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-default-50 rounded-medium">
            <div className="text-tiny font-semibold uppercase tracking-wider text-default-500 mb-2">
              {t("createShipment.from")}
            </div>
            {sender ? (
              <div className="space-y-2">
                {sender.name && (
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-default-500" />
                    <span className="font-medium">{sender.name}</span>
                  </div>
                )}
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-default-500 mt-0.5" />
                  <span>{sender.fullAddress}</span>
                </div>
                {sender.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-default-500" />
                    <span>{sender.phone}</span>
                  </div>
                )}
              </div>
            ) : (
              <span className="text-default-500">-</span>
            )}
          </div>

          <div className="p-4 bg-default-50 rounded-medium">
            <div className="text-tiny font-semibold uppercase tracking-wider text-default-500 mb-2">
              {t("createShipment.to")}
            </div>
            {recipient ? (
              <div className="space-y-2">
                {recipient.name && (
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-default-500" />
                    <span className="font-medium">{recipient.name}</span>
                  </div>
                )}
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-default-500 mt-0.5" />
                  <span>{recipient.fullAddress}</span>
                </div>
                {recipient.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-default-500" />
                    <span>{recipient.phone}</span>
                  </div>
                )}
                {recipient.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-default-500" />
                    <span>{recipient.email}</span>
                  </div>
                )}
              </div>
            ) : (
              <span className="text-default-500">-</span>
            )}
          </div>
        </div>

        {(pickupInstructions || deliveryInstructions) && (
          <div className="space-y-3">
            {pickupInstructions && (
              <div>
                <div className="text-tiny font-semibold uppercase tracking-wider text-default-500 mb-2">
                  {t("orders.pickupInstructions")}
                </div>
                <div className="p-3 bg-default-50 rounded-medium text-sm whitespace-pre-wrap break-words">
                  {pickupInstructions}
                </div>
              </div>
            )}
            {deliveryInstructions && (
              <div>
                <div className="text-tiny font-semibold uppercase tracking-wider text-default-500 mb-2">
                  {t("orders.deliveryInstructions")}
                </div>
                <div className="p-3 bg-default-50 rounded-medium text-sm whitespace-pre-wrap break-words">
                  {deliveryInstructions}
                </div>
              </div>
            )}
          </div>
        )}

        {packages.length > 0 && (
          <div>
            <div className="text-tiny font-semibold uppercase tracking-wider text-default-500 mb-2">
              {t("orderDetails.packagesCount", { count: packages.length })}
            </div>
            <div className="flex flex-wrap gap-2">
              {packages.map((pkg) => (
                <div
                  key={pkg.id}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-default-100 rounded text-sm"
                >
                  <Package className="h-4 w-4" />
                  <span>
                    {pkg.weightGrams
                      ? `${(pkg.weightGrams / 1000).toFixed(1)}kg`
                      : "-"}
                  </span>
                  {pkg.lengthCm && pkg.widthCm && pkg.heightCm && (
                    <span className="text-default-500">
                      {pkg.lengthCm}x{pkg.widthCm}x{pkg.heightCm}cm
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {(() => {
          const proofPhotos =
            shipment?.tasks
              ?.flatMap((t) => t.proof?.mediaItems ?? [])
              .filter((m) => m.url) ?? [];
          if (proofPhotos.length === 0) return null;
          return (
            <div>
              <div className="text-tiny font-semibold uppercase tracking-wider text-default-500 mb-2">
                {t("orders.deliveryPhotos", { count: proofPhotos.length })}
              </div>
              <div className="flex flex-wrap gap-2">
                {proofPhotos.map((photo) => (
                  <a
                    key={photo.id}
                    href={photo.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ProofPhotoPreview
                      photo={photo}
                      alt={t("orders.deliveryProofAlt")}
                    />
                  </a>
                ))}
              </div>
            </div>
          );
        })()}

        <div className="pt-3 border-t border-default-200 space-y-3">
          {shipment?.trackingNumber && (
            <div className="flex items-center min-w-0">
              <a
                href={`/tracking/${shipment.trackingNumber}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline inline-flex items-center gap-1 text-sm font-mono min-w-0"
              >
                <span className="break-all">{shipment.trackingNumber}</span>
                <ExternalLink className="h-3 w-3 shrink-0" />
              </a>
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              {hasOrderRefund(order) && (
                <p className="text-small text-default-500">
                  {t("orders.refundSummary.orderTotal")}
                </p>
              )}
              <span className="text-lg font-semibold">
                {formatCurrency(order.totalCents)}
              </span>
              <OrderPaymentSummary order={order} showBreakdown />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {showPay && (
                <Button
                  size="sm"
                  color="primary"
                  variant="flat"
                  onPress={() => setPayDialogOpen(true)}
                >
                  {t("orders.pay")}
                </Button>
              )}
              {showPrint && (
                <PrintShipmentLabelButton
                  shipmentId={shipment?.id}
                  disabled={!labelUrl}
                  label={t("orders.print")}
                  disabledTitle={t("orders.labelNotReady")}
                  variant="flat"
                />
              )}
              {showChangeRequest && (
                <Button
                  size="sm"
                  color="warning"
                  variant="flat"
                  startContent={<Edit className="h-4 w-4" />}
                  onPress={() => setChangeRequestDialogOpen(true)}
                >
                  {t("orders.requestChanges")}
                </Button>
              )}
              {showCancel && (
                <Button
                  size="sm"
                  color="danger"
                  variant="flat"
                  onPress={() => setCancelDialogOpen(true)}
                >
                  {t(
                    isPreCollectionShipment(status)
                      ? "actions.cancel"
                      : "orders.requestCancellation"
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <PayOrderDialog
        open={payDialogOpen}
        onOpenChange={setPayDialogOpen}
        orderId={order.id}
        totalCents={order.totalCents ?? 0}
        orderNumber={shipment?.shipmentNumber?.toString()}
      />

      {shipment && (
        <ShipmentChangeRequestDialog
          open={changeRequestDialogOpen}
          onOpenChange={setChangeRequestDialogOpen}
          shipment={shipment}
        />
      )}

      <Modal
        isOpen={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        placement="center"
        backdrop="blur"
        disableAnimation
        size="md"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>{t("orders.cancelModalTitle")}</ModalHeader>
              <ModalBody>
                <p className="text-sm text-default-600">
                  {t("orders.cancelModalBody")}
                </p>
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={onClose}>
                  {t("orders.keepOrder")}
                </Button>
                <Button
                  color="danger"
                  onPress={handleCancelConfirm}
                  isDisabled={requestCancellation.isPending}
                  isLoading={requestCancellation.isPending}
                >
                  {requestCancellation.isPending
                    ? t("orders.submitting")
                    : t("orders.requestCancellation")}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
}
